/**
 * Loading the static data the build step wrote into `public/data/`.
 *
 * Everything here is the app's own build output, fetched from the same origin,
 * so it is trusted after a light shape check. Results are memoised: the
 * catalogue and each transcript load at most once per session.
 */
import type MiniSearch from "minisearch";

import { ENGLISH_FIELDS, loadShard, ORIGINAL_FIELDS } from "../lib/search";
import type {
  CatalogEntry,
  SearchDoc,
  ThemesData,
  VideoDoc,
} from "../lib/types";

const EMPTY_THEMES: ThemesData = { harmony: [], wellbeing: [], peace: [] };

function dataUrl(path: string): string {
  return `${import.meta.env.BASE_URL}data/${path}`;
}

/** Fetch and parse a JSON asset. The result is `any` and shape-checked by callers. */
async function fetchJson(url: string) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to load ${url}: ${response.status}`);
  }
  return response.json();
}

let catalogPromise: Promise<CatalogEntry[]> | null = null;

/** The slim catalogue of every video (loaded once). */
export function loadCatalog(): Promise<CatalogEntry[]> {
  catalogPromise ??= fetchJson(dataUrl("catalog.json")).then((data) =>
    Array.isArray(data) ? data : [],
  );
  return catalogPromise;
}

let themesPromise: Promise<ThemesData> | null = null;

/** The three ranked theme lists (loaded once). */
export function loadThemes(): Promise<ThemesData> {
  themesPromise ??= fetchJson(dataUrl("themes.json")).then((data) => ({
    harmony: Array.isArray(data?.harmony) ? data.harmony : EMPTY_THEMES.harmony,
    wellbeing: Array.isArray(data?.wellbeing)
      ? data.wellbeing
      : EMPTY_THEMES.wellbeing,
    peace: Array.isArray(data?.peace) ? data.peace : EMPTY_THEMES.peace,
  }));
  return themesPromise;
}

const videoCache = new Map<string, Promise<VideoDoc | null>>();

/** One video's full record and transcript (cached by id). */
export function loadVideo(id: string): Promise<VideoDoc | null> {
  const cached = videoCache.get(id);
  if (cached !== undefined) {
    return cached;
  }
  const promise = fetchJson(dataUrl(`videos/${id}.json`))
    .then((data) => (data?.id ? data : null))
    .catch(() => null);
  videoCache.set(id, promise);
  return promise;
}

interface SearchManifest {
  readonly shardCount: number;
}

let manifestPromise: Promise<SearchManifest> | null = null;

function loadManifest(): Promise<SearchManifest> {
  manifestPromise ??= fetchJson(dataUrl("search/manifest.json")).then(
    (data) => ({ shardCount: Number(data?.shardCount) || 16 }),
  );
  return manifestPromise;
}

const familyCache = new Map<string, Promise<MiniSearch<SearchDoc>[]>>();

function loadFamily(
  family: "en" | "hi",
  fields: readonly string[],
): Promise<MiniSearch<SearchDoc>[]> {
  const cached = familyCache.get(family);
  if (cached !== undefined) {
    return cached;
  }
  const promise = loadManifest().then((manifest) => {
    const shards: Promise<MiniSearch<SearchDoc>>[] = [];
    for (let shard = 0; shard < manifest.shardCount; shard += 1) {
      shards.push(
        fetch(dataUrl(`search/${family}-${shard}.json`))
          .then((response) => response.text())
          .then((text) => loadShard(text, fields)),
      );
    }
    return Promise.all(shards);
  });
  familyCache.set(family, promise);
  return promise;
}

/** Load (and cache) the English search family. */
export function loadEnglishIndex(): Promise<MiniSearch<SearchDoc>[]> {
  return loadFamily("en", ENGLISH_FIELDS);
}

/** Load (and cache) the original-language search family. */
export function loadOriginalIndex(): Promise<MiniSearch<SearchDoc>[]> {
  return loadFamily("hi", ORIGINAL_FIELDS);
}
